import { useEffect, useState } from "react";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay } from "../lib/money.js";

const SOURCE = {
  ocr: { label: "📷 Receipt photo", note: "you photographed this" },
  import: { label: "🏦 Bank statement", note: "imported from your statement" },
  manual: { label: "✍️ Typed in", note: "entered by hand" },
  stripe: { label: "💳 Stripe", note: "from a Stripe payment" },
  recurring: { label: "🔁 Recurring", note: "posted automatically" },
};

// `onKeep` merges: it keeps THIS entry and soft-deletes the other, moving the
// other's receipt and statement line across. The button used to say "Keep this
// one", which reads as picking a filter rather than removing a ledger row — the
// operator went looking for a merge option and could not find the one he was
// already looking at. It is irreversible from the app, so it now names the
// operation, says what comes with it, and asks before doing it.
function Entry({ t, other, recommended, onKeep, busy }) {
  const s = SOURCE[t.source] || { label: t.source, note: "" };
  const incomingReceipt = !!other?.receipt_id && !t.receipt_id;
  const incomingLine = (other?.bank_lines || 0) > 0 && !t.bank_lines;
  return (
    <div className={`rounded-xl border p-3 ${recommended ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-slate-500">{s.label}</span>
        {recommended && <span className="text-[11px] font-semibold text-emerald-700">suggested</span>}
      </div>
      <p className="mt-1 text-sm font-medium text-slate-800">{t.vendor || "—"}</p>
      <p className="text-xs text-slate-500">
        {t.date} · {t.account_name || t.account_id}
      </p>
      {t.memo && <p className="mt-1 line-clamp-2 text-xs text-slate-400">{t.memo}</p>}
      <p className="mt-1 text-[11px] text-slate-400">
        {t.receipt_id ? "has a receipt image" : "no receipt image"}
        {t.bank_lines > 0 ? " · matched to a statement line" : ""}
      </p>
      <button
        onClick={onKeep}
        disabled={busy}
        className="mt-2 w-full rounded-lg bg-navy-700 py-2 text-xs font-semibold text-white disabled:opacity-50"
      >
        Merge into this one
      </button>
      {(incomingReceipt || incomingLine) && (
        <p className="mt-1 text-center text-[10px] leading-tight text-emerald-700">
          brings the {[incomingReceipt && "receipt image", incomingLine && "statement line"].filter(Boolean).join(" and ")} across
        </p>
      )}
    </div>
  );
}

// One purchase should be one line in the books. This is where a charge that
// got recorded twice — once from the bank, once from its paper receipt — gets
// collapsed back into a single entry, with the photo kept as evidence.
export default function Duplicates() {
  const [pairs, setPairs] = useState(null);
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState([]);

  async function load() {
    const d = await api("/api/money/duplicates").catch(() => ({ pairs: [] }));
    setPairs(d.pairs || []);
  }
  useEffect(() => {
    load();
  }, []);

  async function merge(pair, keepId) {
    const keep = pair.a.id === keepId ? pair.a : pair.b;
    const drop = pair.a.id === keepId ? pair.b : pair.a;
    const brings = [drop.receipt_id && "its receipt image", (drop.bank_lines || 0) > 0 && "its statement line"].filter(Boolean);
    const ok = confirm(
      `Keep the ${SOURCE[keep.source]?.label || keep.source} entry and remove the other?\n\n` +
        `${centsToDisplay(keep.amount_cents)} will appear once in your books` +
        (brings.length ? `, and ${brings.join(" and ")} move${brings.length === 1 ? "s" : ""} onto the entry you keep.` : ".") +
        `\n\nThis cannot be undone from the app.`,
    );
    if (!ok) return;
    const removeId = drop.id;
    setBusy(keepId);
    try {
      await api("/api/money/duplicates", { method: "POST", body: { action: "merge", keep_id: keepId, remove_id: removeId } });
      setDone((d) => [...d, `Merged — ${centsToDisplay(pair.a.amount_cents)} now appears once.`]);
      await load();
    } catch (e) {
      setDone((d) => [...d, `Could not merge: ${e.message || "error"}`]);
    }
    setBusy("");
  }

  async function dismiss(pair) {
    setBusy(pair.a.id);
    try {
      await api("/api/money/duplicates", { method: "POST", body: { action: "dismiss", a_id: pair.a.id, b_id: pair.b.id } });
      await load();
    } catch {
      /* leave it listed — nothing was changed */
    }
    setBusy("");
  }

  return (
    <Shell title="Possible duplicates" back>
      {done.map((m, i) => (
        <p key={i} className="rounded-xl bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {m}
        </p>
      ))}

      {pairs === null && <p className="px-1 text-sm text-slate-400">Checking your ledger…</p>}

      {pairs?.length === 0 && (
        <div className="rounded-2xl bg-white p-6 text-center shadow-sm">
          <p className="text-sm font-medium text-slate-700">No duplicates found ✓</p>
          <p className="mt-1 text-xs text-slate-400">
            Every charge in your books appears once, within four days and the same amount and type. If the reconcile audit still
            lists pairs, they were dismissed here as separate charges.
          </p>
        </div>
      )}

      {pairs?.map((pair) => (
        <section key={`${pair.a.id}-${pair.b.id}`} className="rounded-2xl bg-white p-4 shadow-sm">
          <p className="text-lg font-semibold text-navy-700">{centsToDisplay(pair.a.amount_cents)}</p>
          <p className="mt-0.5 text-xs text-slate-500">{pair.why}</p>

          {pair.both_bank_matched && (
            <div className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
              <p className="font-semibold">Both sides are matched to a statement line, so this one is your call.</p>
              <p className="mt-1">
                Usually that means the bank really did charge you twice and both entries are real. But it also happens when one
                statement gets imported twice under two different account names — then it is one charge wearing two bank lines.
                Check the statement: if the charge appears <strong>once</strong>, merge them.
              </p>
            </div>
          )}

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Entry t={pair.a} other={pair.b} recommended={pair.suggested_keep_id === pair.a.id} busy={!!busy} onKeep={() => merge(pair, pair.a.id)} />
            <Entry t={pair.b} other={pair.a} recommended={pair.suggested_keep_id === pair.b.id} busy={!!busy} onKeep={() => merge(pair, pair.b.id)} />
          </div>

          <p className="mt-2 text-[11px] text-slate-400">
            Merging keeps the entry you choose, removes the other from your books, and moves its receipt image and statement line onto
            the one you kept. Nothing is erased — a removed entry stays recoverable and the change is written to the activity log —
            but there is no undo button.
          </p>
          <button
            onClick={() => dismiss(pair)}
            disabled={!!busy}
            className="mt-2 w-full rounded-lg border border-slate-300 py-2 text-xs font-semibold text-slate-500 disabled:opacity-50"
          >
            These are two separate charges — keep both
          </button>
        </section>
      ))}
    </Shell>
  );
}
